import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn, getTestId } from '../../utils';
import { GLOBAL_HEIGHT, GlobalSize } from '../../types/global-size';

type BaseInputAttributes = Pick<
  React.InputHTMLAttributes<HTMLInputElement>,
  | 'id'
  | 'name'
  | 'placeholder'
  | 'tabIndex'
  | 'autoFocus'
  | 'autoComplete'
  | 'readOnly'
  | 'required'
  | 'min'
  | 'max'
  | 'step'
  | 'pattern'
  | 'maxLength'
  | 'minLength'
  | 'onKeyDown'
  | 'onKeyUp'
  | 'onKeyPress'
  | 'onMouseDown'
  | 'onMouseUp'
  | 'onMouseEnter'
  | 'onMouseLeave'
  | 'onMouseOver'
  | 'onClick'
  | 'className'
  | 'disabled'
  | 'type'
  | 'value'
  | 'defaultValue'
  | 'onChange'
  | 'onBlur'
  | 'onFocus'
  | 'inputMode'
  | 'onInput'
>;

const inputVariants = cva(
  `bg-background-input w-full transition-all rounded-lg border flex flex-row flex-nowrap`,
  {
    variants: {
      hasError: {
        true: '',
        false: 'border-border-base focus-within:!border-primary-base'
      },
      variant: {
        default: '',
        website: '[&>div:first-child]:border-r-2 [&>*:first-child]:border-r-border-base',
        currency: ''
      }
    },
    compoundVariants: [
      // Default / Currency focus on wrapper
      {
        hasError: false,
        variant: ['default', 'currency'],
        className: 'focus-within:ring-4 focus-within:!ring-border-base'
      },
      {
        hasError: true,
        variant: ['default', 'currency'],
        className: 'border-error-text focus-within:ring-4 focus-within:!ring-error-bg'
      },
      // WebsiteField — no focus-within, the input itself will handle focus, also note that this variant is mostly for showcase purposes and isnt actively supported
      {
        hasError: false,
        variant: 'website',
        className:
          '[&>div:last-child:focus-within]:ring-4 [&>div:last-child:focus-within]:ring-border-base'
      },
      {
        hasError: true,
        variant: 'website',
        className:
          '[&>div:last-child:focus-within]:ring-4 [&>div:last-child:focus-within]:ring-error-bg border-border-base [&>div:last-child]:border-error-text'
      }
    ],
    defaultVariants: {
      hasError: false,
      variant: 'default'
    }
  }
);

type InputVariants = VariantProps<typeof inputVariants>;

export interface InputProps extends BaseInputAttributes, InputVariants {
  leftContent?: React.ReactNode;
  rightContent?: React.ReactNode;
  leftContentClassName?: string;
  rightContentClassName?: string;
  height?: GlobalSize;
  testId?: string;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      leftContent,
      variant,
      hasError,
      rightContent,
      leftContentClassName,
      rightContentClassName,
      height = 'md',
      testId,
      ...props
    },
    ref
  ) => {
    return (
      <div
        className={cn(
          inputVariants({ hasError, variant }),
          GLOBAL_HEIGHT[height],
          'px-sm',
          className
        )}
      >
        {leftContent && (
          <div
            className={cn(
              leftContentClassName,
              'flex items-center justify-center text-foreground-muted'
            )}
          >
            {leftContent}
          </div>
        )}
        <div className='transition-all w-[100%] rounded-r-lg flex flex-row flex-nowrap border-2 border-transparent'>
          <input
            {...props}
            data-testid={getTestId({ testId, id: props.id, name: props.name })}
            aria-invalid={hasError || undefined}
            aria-required={props.required || undefined}
            className='bg-transparent w-[100%] text-sm border-transparent placeholder:text-foreground-disabled text-foreground-base font-normal focus:outline-none focus:border-transparent'
            ref={ref}
          ></input>
          {rightContent && (
            <div
              className={cn(
                rightContentClassName,
                'text-foreground-muted flex items-center justify-center flex-row flex-nowrap'
              )}
            >
              {rightContent}
            </div>
          )}
        </div>
      </div>
    );
  }
);

Input.displayName = 'Input';
